/**
 * Coupons and promotions as they apply to single items of an order.
 *
 * The promotion engine reports what it took off an order as a list of
 * promotions, each naming the items it touched under `affectedItems`. A
 * promotion redeemed with a code is a coupon, every other one applied on its
 * own. A POS order carries that list on `charges.promotionDetails`, a website
 * order on `promotionInfo.appliedPromotions`.
 */

/** Blank text and a bare "()" name nothing. */
const cleanLabel = (value) => {
  const text = String(value ?? '').trim();
  if (!text || text.replace(/\s+/g, '') === '()') return '';
  return text;
};

const firstLabel = (...values) => {
  for (const value of values) {
    const text = cleanLabel(value);
    if (text) return text;
  }
  return '';
};

const moneyCents = (value) => Math.round(value * 100);

const toList = (value) => {
  if (Array.isArray(value)) return value;
  return value ? [value] : [];
};

/**
 * An order line is known by one of several ids depending on who wrote it, so
 * all of them are read when matching the engine's item id.
 */
const productMatchIds = (product) => {
  const ids = new Set();
  [
    product?.uuid,
    product?.itemUuid,
    product?.itemId,
    product?.externalId,
    product?.internalId,
    product?._id,
  ].forEach((value) => {
    const text = cleanLabel(value);
    if (text) ids.add(text);
  });
  return ids;
};

/**
 * What one line of the order comes to, which is the money the engine states as
 * the line's `originalPrice`: the item and the modifiers chosen on it, each
 * counted once per item ordered, less the ones granted free. An item can carry
 * its whole price in a modifier — a $0 pizza of a $3 size — so the item's own
 * price alone would not find it.
 */
const lineTotal = (product) => {
  const qty = Math.abs(Number(product?.quantity) || 0) || 1;
  const modifiers = toList(product?.modifier).reduce(
    (total, group) =>
      total +
      toList(group?.list).reduce((groupTotal, option) => {
        const count =
          Math.abs(Number(option?.count) || 0) -
          Math.abs(Number(option?.freeCount) || 0);
        if (count <= 0) return groupTotal;
        return groupTotal + Math.abs(Number(option?.price) || 0) * count;
      }, 0),
    0
  );
  return (Math.abs(Number(product?.price) || 0) + modifiers) * qty;
};

/**
 * The items a promotion was taken off, as the engine named them. Free delivery
 * is money off the delivery fee rather than off the item, and the engine still
 * names the item that qualified the order for it — which is the item the POS
 * states it under, so it is stated under that item here too.
 */
const affectedItemsOf = (promotion) =>
  toList(promotion?.affectedItems)
    .map((item) => ({
      id: cleanLabel(item?.itemUuid),
      amount: Math.abs(Number(item?.discountAmount) || 0),
      originalPrice: Math.abs(Number(item?.originalPrice) || 0),
      adjustedPrice: Math.abs(Number(item?.adjustedPrice) || 0),
    }))
    .filter((item) => item.amount > 0);

/**
 * An empty `promotionDetails` array is authoritative and does not fall through
 * to the website order's list.
 */
const promotionList = (order) => {
  const details = order?.charges?.promotionDetails;
  console.log(details, '>detailsdetailsdetails');
  if (Array.isArray(details)) return details;
  return toList(order?.promotionInfo?.appliedPromotions);
};

/**
 * A coupon is the promotion its code unlocked. The POS states that code on the
 * promotion; a website order instead names the redeemed promotion at the top of
 * `promotionInfo`.
 */
const isCouponPromotion = (promotion, order) => {
  if (cleanLabel(promotion?.promotionCode)) return true;
  const redeemed = cleanLabel(order?.promotionInfo?.promotionUuid);
  return !!redeemed && cleanLabel(promotion?.promotionUuid) === redeemed;
};

const toChargeRecord = (promotion, index, order) => {
  const affected = affectedItemsOf(promotion);
  const amount =
    Math.abs(Number(promotion?.discountAmount) || 0) ||
    affected.reduce((sum, item) => sum + item.amount, 0);
  if (!amount) return null;

  return {
    key: cleanLabel(promotion?.promotionUuid) || `promotion-${index}`,
    isCoupon: isCouponPromotion(promotion, order),
    name: firstLabel(promotion?.promotionName, promotion?.displayLabel),
    amount,
    affected,
  };
};

/**
 * Every coupon and promotion of an order, each with the items it was taken off.
 * A coupon the POS granted as a discount of its own is added when the engine
 * did not already report it.
 */
export const promotionChargeRecords = (order) => {
  const records = promotionList(order)
    .map((promotion, index) => toChargeRecord(promotion, index, order))
    .filter(Boolean);

  const covered = new Set(
    records.flatMap((record) => record.affected.map((item) => item.id))
  );

  toList(order?.charges?.discountDetails).forEach((detail, index) => {
    if (
      String(detail?.appliesTo || '').toLowerCase() === 'order' ||
      detail?.isCashDiscount ||
      detail?.freeDelivery
    ) {
      return;
    }
    const itemId = cleanLabel(detail?.itemUuid);
    if (!itemId || covered.has(itemId)) return;

    const couponId = cleanLabel(detail?.couponId);
    const name = firstLabel(
      detail?.couponTitle,
      detail?.couponName,
      detail?.couponCode,
      detail?.discountCode
    );
    if (!couponId && !name) return;

    const amount = Math.abs(Number(detail?.discountAmount) || 0);
    if (!amount) return;

    records.push({
      key: couponId || `item-coupon-${itemId}-${index}`,
      isCoupon: true,
      name,
      amount,
      affected: [{ id: itemId, amount, originalPrice: 0, adjustedPrice: 0 }],
    });
  });

  return records;
};

/**
 * Assigns each discounted line the engine reported to one item of the order.
 *
 * `affectedItems.itemUuid` is the catalog id of the item, which an order line
 * written before the engine existed does not carry, so an id match often fails.
 * Those lines still state the line total before and after the discount — `3 x
 * $5` is an `originalPrice` of 15, then 12 — so an item is also matched by the
 * money it stands at, which the promotions already applied to it move along.
 *
 * Two items of an order can stand at the same money, and then there is nothing
 * to tell them apart by. Each discounted line takes the first item still free
 * at that price: both read the same on the bill, and leaving the discount off
 * the order entirely would state less than is known.
 */
export const createItemPromotionLookup = (order) => {
  const records = promotionChargeRecords(order);
  const products = toList(order?.products);
  const linesByIndex = products.map(() => []);
  const baseTotal = products.map(lineTotal);
  const runningPrice = [...baseTotal];
  const deliveredCopies = new Set();

  const at = (prices, originalPrice, used) =>
    prices.findIndex(
      (price, index) =>
        !used.has(index) && moneyCents(price) === moneyCents(originalPrice)
    );

  const firstPriceMatch = (originalPrice, used) => {
    const reduced = at(runningPrice, originalPrice, used);
    if (reduced >= 0) return reduced;
    // Free delivery takes nothing off the item, so the engine states the line
    // at the money it started at rather than at what the promotions before it
    // left it standing at.
    return at(baseTotal, originalPrice, used);
  };

  records.forEach((record) => {
    const used = new Set();
    record.affected.forEach((item, affectedIndex) => {
      let productIndex = products.findIndex(
        (product, index) =>
          !used.has(index) && productMatchIds(product).has(item.id)
      );
      if (productIndex < 0 && item.originalPrice) {
        productIndex = firstPriceMatch(item.originalPrice, used);
      }
      if (productIndex < 0) return;

      used.add(productIndex);
      // Only ever downwards: a promotion that left the line where it was, as
      // free delivery does, must not undo what an earlier one took off it.
      if (
        item.adjustedPrice &&
        moneyCents(item.adjustedPrice) < moneyCents(runningPrice[productIndex])
      ) {
        runningPrice[productIndex] = item.adjustedPrice;
      }
      linesByIndex[productIndex].push({
        key: `${record.key}:${affectedIndex}`,
        isCoupon: record.isCoupon,
        name: record.name,
        amount: item.amount,
      });
    });
  });

  return {
    linesFor(product) {
      const direct = products.indexOf(product);
      if (direct >= 0) return linesByIndex[direct];

      const id = cleanLabel(product?.uuid) || cleanLabel(product?._id);
      const bucket = products.findIndex(
        (item) =>
          id && (cleanLabel(item?.uuid) === id || cleanLabel(item?._id) === id)
      );
      if (bucket < 0 || deliveredCopies.has(id)) return [];
      deliveredCopies.add(id);
      return linesByIndex[bucket];
    },
  };
};

/** "Coupon (name)" / "Promotion (name)", or the bare kind when it has no name. */
export const itemPromotionTitle = (kindLabel, name) => {
  const label = cleanLabel(name);
  return label ? `${kindLabel} (${label})` : kindLabel;
};
